import { MacBrand, MAC_LOGO_URL } from '@/components/MacBrand';
import { Seo } from "@/components/Seo";
import { Reveal, HoldingsWordmark, SectionTitle, PillButton } from "@/components/primitives";
import { FOUNDER_QUOTE_LEAD, FOUNDER_QUOTE_EMPH } from "@/lib/copy";

const DESCRIPTIONS = {
  "venture-mac": "MAC — My Assistant CEO coordinates work across the ecosystem, consolidating documented findings and identifying decisions that require the Founder’s judgment.",
  "venture-steward-stone": "Steward & Stone examines organizational practice and provides personal and business income-tax preparation tools, connecting informed decisions with the evidence and responsibilities behind them.",
  "venture-strategic-assets": "Strategic Assets examines property, recovery, acquisition, and asset-based circumstances to determine which lawful possibilities the evidence supports.",
  "venture-nirva-life": "Nirva Life develops Nervous System Intelligence: understanding the influences that shape perception, interpretation, and response, so intentional choices align with the person one seeks to become.",
  "venture-foundation": "The Jennae Michelle Foundation will direct resources toward experiences and initiatives that help people recognize and develop their own capabilities."
};

const VENTURES = [
  { name: "MAC — My Assistant CEO", to: "https://mac.nirvalife.com", img: MAC_LOGO_URL, testid: "venture-mac", square: false, bg: "var(--jmh-black)", external: true },
  { name: "Steward & Stone — Strategic & Operational Advisory", to: "https://stewardandstone.jennaemichelle.com", img: "/brand/steward-stone-official.webp", testid: "venture-steward-stone", square: false, bg: "var(--jmh-navy)", external: true },
  { name: "Strategic Assets — Intelligence, Recovery, Acquisitions & Real Estate", to: "https://strategicassets.jennaemichelle.com", img: "/brand/strategic-assets-official.webp", testid: "venture-strategic-assets", square: true, bg: "var(--jmh-navy)", external: true },
  { name: "Nirva Life — Nervous System Intelligence", to: "https://nirvalife.com", img: "/brand/nirva-official.webp", testid: "venture-nirva-life", square: true, bg: "var(--jmh-black)", external: true },
  { name: "Jennae Michelle Foundation", to: "https://foundation.jennaemichelle.com", img: "/brand/foundation-official.webp", testid: "venture-foundation", square: false, bg: "#f3f0e7", external: true },
];


const PRESENTATION = {
  "venture-nirva-life": { photo: "/brand/nirva-life-editorial-portfolio-v3.jpeg", alt: "A woman with dark curls in an ivory suit walking along a sunlit city street lined with pale stone buildings", crop: 1 },
  "venture-steward-stone": { photo: "/brand/steward-portfolio-advisory-team.jpeg", alt: "A consultant discussing healthcare strategy with colleagues in a meeting", crop: 2 },
  "venture-strategic-assets": { photo: "/brand/strategic-property-options-portfolio.jpeg", alt: "A property advisor reviewing home plans and real-estate documents with a couple", crop: 3 },
  "venture-foundation": { crop: 4 },
};
const VentureLogo = ({ v }) => (
  <div className={`jmh-logo-art jmh-logo-crop-${PRESENTATION[v.testid].crop}`}>
    <img decoding="async" src={v.img} alt={v.name} loading="lazy" />
  </div>
);
const VentureCard = ({ v }) => {
  const presentation = PRESENTATION[v.testid];
  return (
    <article className="corner-frame jmh-portfolio-card jmh-overview-card" data-testid={v.testid}>
      <div className="jmh-portfolio-media">
        {presentation?.photo
          ? <img decoding="async" className="jmh-business-photo" src={presentation.photo} alt={presentation.alt} loading="lazy" />
          : v.testid === "venture-mac"
            ? <div className="jmh-mac-lockup"><MacBrand className="!h-auto !w-full !max-w-none" /></div>
            : <div className="jmh-overview-foundation"><VentureLogo v={v} /></div>}
      </div>
      <div className="jmh-portfolio-copy">
        {presentation?.photo && <div className="jmh-overview-brand"><VentureLogo v={v} /></div>}
        <h3 className={presentation?.photo ? "sr-only" : "jmh-overview-name"}>{v.name}</h3>
        <p className="jmh-venture-description text-[var(--bone)]/85 leading-relaxed">{DESCRIPTIONS[v.testid]}</p>
        {v.external
          ? <PillButton href={v.to} external testid={`${v.testid}-open`}>{v.testid === "venture-foundation" ? "Explore the Foundation" : `Visit ${v.testid === "venture-mac" ? "MAC" : v.testid === "venture-nirva-life" ? "Nirva Life" : v.testid === "venture-steward-stone" ? "Steward & Stone" : "Strategic Assets"}`}</PillButton>
          : <PillButton to={v.to} testid={`${v.testid}-open`}>Explore the Foundation</PillButton>}
      </div>
    </article>
  );
};

export default function Portfolio() {
  return (
    <div className="glow-black jmh-portfolio-page">
      <Seo title="Our Portfolio" description="Affiliated Ventures of Jennae Michelle Holdings — Steward & Stone, Nirva Life, Strategic Assets, MAC, and the Jennae Michelle Foundation." />

      <section className="jmh-portfolio-intro jmh-section jmh-page-opening">
        <div className="jmh-content-shell jmh-overview-opening">
          <div className="jmh-overview-holdings">
            <HoldingsWordmark />
            <p className="font-serif2 italic text-[var(--bone)]/90">{FOUNDER_QUOTE_LEAD}<span className="gold-text font-serif2">{FOUNDER_QUOTE_EMPH}</span></p>
          </div>
          <div className="jmh-portfolio-introduction text-[var(--bone)]/85">
            <h1 className="font-display gold-text">Our Portfolio</h1>
            <p>Each organization addresses a distinct need identified through lived experience. Their responsibilities are connected by a shared commitment to understanding people and circumstances before determining an appropriate response.</p>
            <p><span className="text-[var(--bone)]">Jennae Michelle Holdings</span> provides the governance and organizational structure that connects their work while preserving each entity’s distinct purpose and responsibility.</p>
          </div>
        </div>
      </section>

      <section className="jmh-section">
        <div className="jmh-content-shell">
          <Reveal><SectionTitle testid="portfolio-title">Affiliated Ventures</SectionTitle></Reveal>

          <div className="jmh-portfolio-grid jmh-overview-grid">
            {VENTURES.map((v) => (
              <VentureCard key={v.name} v={v} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

